import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayScrollLock } from './useOverlayScrollLock';
import { X } from 'lucide-react';

interface DrawerProps {
    children: ReactNode;
    title: string;
    subTitle?: ReactNode;
    onClose: () => void;
    widthClass?: string;
    backdrop?: boolean;
    backdropStyle?: CSSProperties;
    panelClassName?: string;
    headerClassName?: string;
}

export const Drawer = ({
    title,
    subTitle,
    children,
    onClose,
    widthClass = 'w-full max-w-xl',
    backdrop = true,
    backdropStyle,
    panelClassName = '',
    headerClassName = 'px-5 py-4 items-start',
}: DrawerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [condensed, setCondensed] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    useOverlayScrollLock(scrollRef, backdrop);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => setIsOpen(true));
        return () => window.cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const portalRoot = typeof document !== 'undefined' ? document.getElementById('outside-root') : null;
    if (!portalRoot) {
        return null;
    }

    return createPortal(
        <div
            className={`fixed inset-0 z-[1000] ${backdrop ? '' : 'pointer-events-none'}`}
            role="dialog"
            aria-modal={backdrop ? 'true' : undefined}
        >
            {backdrop && (
                <div
                    className={`absolute inset-0 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                    style={{ background: 'var(--sf-overlay-backdrop)', backdropFilter: 'blur(8px)', ...backdropStyle }}
                    onClick={onClose}
                />
            )}
            <div
                className={`sf-overlay-panel pointer-events-auto absolute right-0 top-0 h-full ${widthClass} ${
                    panelClassName || ''
                } transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                    <div className={`sticky top-0 z-[1] flex justify-between gap-3 border-b border-white/5 bg-base-100/75 backdrop-blur ${headerClassName}`}>
                        <div>
                            <h2 className={`sf-overlay-title ${condensed ? 'is-condensed' : ''}`}>{title}</h2>
                            {subTitle && <p className="mt-1 text-sm text-base-content/60">{subTitle}</p>}
                        </div>
                        <button
                            type="button"
                            className="sf-icon-button sf-close-button btn btn-circle btn-sm"
                            onClick={onClose}
                            aria-label="Fechar painel"
                        >
                            <X aria-hidden="true" />
                        </button>
                    </div>
                    <div ref={scrollRef} data-overlay-scroll onScroll={(event) => setCondensed(event.currentTarget.scrollTop > 8)} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
                        {children}
                    </div>
                </div>
            </div>
        </div>,
        portalRoot,
    );
};
